import { useLocalParticipant } from "@livekit/components-react";
import { BackgroundBlur, VirtualBackground } from "@livekit/track-processors";
import { isLocalTrack, type LocalTrackPublication } from "livekit-client";
import { ImageIcon, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { uiStore } from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";

const DEFAULT_BLUR_RADIUS = 20;
const DEBOUNCE_MS = 150;

const BACKGROUND_IMAGES = [
	{
		name: "Htctw 1",
		url: `${import.meta.env.BASE_URL}htctw_celebration.jpg`,
		icon: ImageIcon,
	},
	{
		name: "Htctw 2",
		url: `${import.meta.env.BASE_URL}htctw_standard.webp`,
		icon: ImageIcon,
	},
	{
		name: "Office 1",
		url: `${import.meta.env.BASE_URL}video_background_1.webp`,
		icon: ImageIcon,
	},
	{
		name: "Office 2",
		url: `${import.meta.env.BASE_URL}video_background_2.webp`,
		icon: ImageIcon,
	},
	{
		name: "Office 3",
		url: `${import.meta.env.BASE_URL}video_background_3.webp`,
		icon: ImageIcon,
	},
];

export function BackgroundEffectApplier() {
	const backgroundSettings = useStore(
		uiStore,
		(state) => state.backgroundSettings
	);
	const { cameraTrack } = useLocalParticipant();

	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isProcessingRef = useRef(false);
	const pendingSettingsRef = useRef<typeof backgroundSettings | null>(null);

	useEffect(() => {
		const track = (cameraTrack as LocalTrackPublication)?.track;
		if (!isLocalTrack(track)) return;

		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		const settingsToApply = { ...backgroundSettings };

		// Debounced so rapid clicks on the background swatches batch into one apply.
		debounceTimerRef.current = setTimeout(() => {
			const applyProcessor = async () => {
				// A processor swap is in flight, so hand these settings to it instead.
				if (isProcessingRef.current) {
					pendingSettingsRef.current = settingsToApply;
					return;
				}

				isProcessingRef.current = true;

				try {
					if (settingsToApply.type === "blur") {
						await track.setProcessor(
							BackgroundBlur(settingsToApply.blurAmount || DEFAULT_BLUR_RADIUS)
						);
					} else if (
						settingsToApply.type === "image" &&
						settingsToApply.imageUrl
					) {
						await track.setProcessor(
							VirtualBackground(settingsToApply.imageUrl)
						);
					} else {
						await track.stopProcessor();
					}
				} catch {
					// Ignore errors
				} finally {
					isProcessingRef.current = false;

					if (pendingSettingsRef.current) {
						const pending = pendingSettingsRef.current;
						pendingSettingsRef.current = null;
						uiStore.getState().setBackgroundSettings(pending);
					}
				}
			};

			applyProcessor();
		}, DEBOUNCE_MS);

		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [
		cameraTrack,
		backgroundSettings.type,
		backgroundSettings.imageUrl,
		backgroundSettings.blurAmount,
	]);

	return null;
}

export function BackgroundSelector() {
	const backgroundSettings = useStore(
		uiStore,
		(state) => state.backgroundSettings
	);
	const setBackgroundSettings = useStore(
		uiStore,
		(state) => state.setBackgroundSettings
	);

	const selectBackground = (
		type: "none" | "blur" | "image",
		imageUrl?: string
	) => {
		setBackgroundSettings({
			type,
			imageUrl: imageUrl || null,
		});
	};

	const isSelected = (type: string, imageUrl?: string) => {
		if (type === "image") {
			return (
				backgroundSettings.type === "image" &&
				backgroundSettings.imageUrl === imageUrl
			);
		}
		return backgroundSettings.type === type;
	};

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<Label className="text-sm">Background Effects</Label>
				<div className="flex flex-wrap gap-2">
					<Button
						className={cn(
							"gap-2",
							isSelected("none") && "ring-2 ring-primary ring-offset-2"
						)}
						onClick={() => selectBackground("none")}
						size="sm"
						variant={isSelected("none") ? "default" : "outline"}
					>
						<X className="size-4" />
						None
					</Button>
					<Button
						className={cn(
							"gap-2",
							isSelected("blur") && "ring-2 ring-primary ring-offset-2"
						)}
						onClick={() => selectBackground("blur")}
						size="sm"
						variant={isSelected("blur") ? "default" : "outline"}
					>
						<ImageIcon className="size-4" />
						Blur
					</Button>
					{BACKGROUND_IMAGES.map((image) => {
						const Icon = image.icon;
						return (
							<Button
								className={cn(
									"gap-2",
									isSelected("image", image.url) &&
										"ring-2 ring-primary ring-offset-2"
								)}
								key={image.name}
								onClick={() => selectBackground("image", image.url)}
								size="sm"
								variant={isSelected("image", image.url) ? "default" : "outline"}
							>
								<Icon className="size-4" />
								{image.name}
							</Button>
						);
					})}
				</div>
			</div>

			{backgroundSettings.type === "blur" && <BlurSlider />}
		</div>
	);
}

function BlurSlider() {
	const backgroundSettings = useStore(
		uiStore,
		(state) => state.backgroundSettings
	);
	const setBackgroundSettings = useStore(
		uiStore,
		(state) => state.setBackgroundSettings
	);

	// Local state so the slider tracks the drag without waiting on the store.
	const [localValue, setLocalValue] = useState(backgroundSettings.blurAmount);

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<Label htmlFor="blur-amount">Blur Intensity</Label>
				<span className="text-muted-foreground text-sm">{localValue}</span>
			</div>
			<Slider
				className="w-full"
				id="blur-amount"
				max={20}
				min={1}
				onValueChange={(value) => setLocalValue(value[0])}
				onValueCommit={(value) =>
					setBackgroundSettings({ blurAmount: value[0] })
				}
				step={1}
				value={[localValue]}
			/>
		</div>
	);
}
